<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { watch } from "vue";
import { z } from "zod";

import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";

import InputText from "primevue/inputtext";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  program?: SiteProgramOverview;
  isPending?: boolean;
}

interface IEmits {
  (e: "submit", name: string): void;
}

const visible = defineModel<boolean>();
const emits = defineEmits<IEmits>();
const props = withDefaults(defineProps<IProps>(), {
  isPending: false,
});

const { t, k, rawT } = useTypedIntl();

const renameProgramFormSchema = z.object({
  name: z.string().min(1, "required"),
});

const { handleSubmit, errors, defineField, setFieldValue } = useForm({
  validationSchema: toTypedSchema(renameProgramFormSchema),
  initialValues: {
    name: props.program?.name ?? "",
  },
});

const [name] = defineField("name");

const submit = handleSubmit((values) => {
  if (props.isPending) return;

  emits("submit", values.name);
});

watch(
  () => props.program,
  (newValue) => {
    if (!newValue) {
      setFieldValue("name", "");
      return;
    }

    setFieldValue("name", newValue.name);
  },
  { immediate: true },
);
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    modal
    size="small"
  >
    <template #header>
      <div class="flex flex-col">
        <h4 class="flex w-full font-semibold">
          {{ t(k.control.programming.renaming.title) }}
        </h4>
      </div>
    </template>

    <form @submit="submit">
      <label class="font-medium">{{ t(k.control.programming.creation.programName) }}</label>
      <InputText
        v-model="name"
        autofocus
        data-test-id="program-renaming-name-input"
        :disabled="props.isPending"
        :invalid="!!errors.name"
      />
      <small
        v-if="errors.name"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${errors.name}`) }}
      </small>
    </form>

    <template #footer>
      <div class="grid grid-flow-col grid-cols-2 gap-2 w-full">
        <HospitalityButton
          data-test-id="program-copy-cancel-button"
          :disabled="props.isPending"
          severity="secondary"
          @click="visible = false"
        >
          <i class="pi pi-arrow-left mr-2" />
          {{ t(k.global.misc.cancel) }}
        </HospitalityButton>
        <HospitalityButton
          data-test-id="program-edition-submit-button"
          :disabled="props.isPending"
          :label="t(k.global.misc.submit)"
          :loading="props.isPending"
          @click="submit"
        />
      </div>
    </template>
  </BaseDialog>
</template>
